<template>
    <page-title
      title="Bulk Tagging"
      section="Clients">
    </page-title>
    
    <div class="container mx-auto grow">
      <h1 class="main-page-title">
        <span class="subtitle">Clients</span>
        Tag Clients via CSV/Excel
      </h1>

      <div class="split-view-left">
        <div class="pb-4 md:pb-0 md:w-1/3 shrink-0">
          <div class="mt-1 text-gray-600 text-sm space-y-6">
            <p>Upload a CSV or Excel file to tag existing clients or create new leads.</p>

            <div>
              <span class="font-medium">Instructions:</span>
              <ul class="list-disc list-outside pl-4 pt-1 leading-relaxed">
                <li>Prepare a CSV or Excel file with client information.</li>
                <li>Upload the file and map the columns to the appropriate fields.</li>
                <li>Select or create a tag to apply to the clients.</li>
                <li>Choose whether to add or remove the tag.</li>
                <li>At least one of Email or Mobile Phone must be mapped.</li>
              </ul>
            </div>

            <div>
              <span class="font-medium">What happens:</span>
              <ul class="list-disc list-outside pl-4 pt-1 leading-relaxed">
                <li>Existing clients will be matched by email or mobile phone.</li>
                <li>If adding tags and no matching client is found, a new lead will be created and tagged.</li>
                <li>If removing tags, only existing clients will be affected.</li>
                <li>All operations are scoped to your current team.</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="flex-1">
          <client-tag-excel
            ref="clientTagExcel"
            @success="handleSuccess"
            @error="handleError"
          />
          
          <!-- Results Section (shown after submission) -->
          <div v-if="showResults" class="mt-8 p-4 bg-white shadow rounded-lg">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Processing Results</h3>
            
            <div v-if="isProcessing" class="flex justify-center items-center py-8">
              <loader class="w-12 h-12 text-gray-500"></loader>
              <span class="ml-4 text-gray-700">Processing your file...</span>
            </div>
            
            <div v-else-if="processError" class="text-red-600 py-4">
              <p class="font-medium">Error:</p>
              <p>{{ processError }}</p>
              <div class="mt-4">
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-loopspark-600 hover:bg-loopspark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-loopspark-500"
                  @click="resetResults"
                >
                  Try Again
                </button>
              </div>
            </div>
            
            <div v-else-if="processResult" class="space-y-4">
              <div class="flex items-center text-green-600">
                <svg-icon name="check" class="w-5 h-5 mr-2"></svg-icon>
                <span class="font-medium">Operation Successful!</span>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-gray-50 p-4 rounded">
                  <p class="text-sm text-gray-500">Clients Tagged</p>
                  <p class="text-2xl font-bold">{{ processResult.clientsTagged }}</p>
                </div>
                
                <div class="bg-gray-50 p-4 rounded">
                  <p class="text-sm text-gray-500">Leads Created</p>
                  <p class="text-2xl font-bold">{{ processResult.leadsCreated }}</p>
                </div>
              </div>
              
              <p class="text-gray-700">{{ processResult.message }}</p>
              
              <div class="mt-4">
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-loopspark-600 hover:bg-loopspark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-loopspark-500"
                  @click="resetResults"
                >
                  Process Another File
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import PageTitle from '@/components/navbar/PageTitle.vue';
import ClientTagExcel from '@/views/Clients/Components/ClientTagExcel.vue';
import Loader from '@/components/layout/Loader.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { useBatchTagClientsMutation } from '@/graphql/queries/ql/composables';

// Results state
const showResults = ref(false);

// GraphQL mutation
const {
  mutate: batchTagClients,
  loading: isProcessing,
  error: processError,
  data: processResult
} = useBatchTagClientsMutation();

// Handle successful form submission
const handleSuccess = async (payload) => {
  showResults.value = true;
  
  try {
    const result = await batchTagClients({
      clients: payload.clients,
      tagNames: payload.tagNames,
      action: payload.action
    });
    
    // Update the Excel component with the result status
    if (clientTagExcel.value) {
      clientTagExcel.value.updateItemStatus(result.success);
    }
  } catch (error) {
    console.error('Error processing batch tag operation:', error);
    // The processError state is already set by the composable
    
    // Update the Excel component with the error status
    if (clientTagExcel.value) {
      clientTagExcel.value.updateItemStatus(false);
    }
  }
};

// Handle error from the Excel component
const handleError = (error) => {
  showResults.value = true;
  processError.value = error;
};

// Component ref
const clientTagExcel = ref(null);

// Reset results to process another file
const resetResults = () => {
  showResults.value = false;
  
  // Reset the form
  if (clientTagExcel.value) {
    clientTagExcel.value.reset();
  }
};
</script>